import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Users, TrendingUp, Filter, MapPin, Calendar, ChevronDown, ChevronRight } from 'lucide-react';

const CollapsibleSection = ({ title, icon, count, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="hf-map-card">
      <h3
        className="hf-section-title"
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        {icon}
        {title}
        {count !== undefined && (
          <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 400, marginLeft: '0.5rem' }}>
            ({count})
          </span>
        )}
      </h3>
      {open && children}
    </div>
  );
};

const MarketingMetrics = ({ candidateData }) => {
  const [hubspotContacts, setHubspotContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Fetch HubSpot segment data
  const fetchHubspot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/hubspot-segment');
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      console.log('HubSpot segment data:', data.total, 'contacts');
      console.log('Sample contacts:', data.contacts.slice(0, 3));
      setHubspotContacts(data.contacts || []);
      setLastFetched(data.fetchedAt);
    } catch (err) {
      console.error('Failed to fetch HubSpot data:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchHubspot();
  }, [fetchHubspot]);

  // Cross-reference HubSpot leads with candidate pipeline
  const analysis = useMemo(() => {
    if (hubspotContacts.length === 0 || !candidateData || candidateData.length === 0) {
      return null;
    }

    // Build email lookup from candidate data (lowercase)
    const candidateByEmail = {};
    candidateData.forEach(r => {
      const email = (r['Email'] || r['email'] || '').toLowerCase().trim();
      if (email) {
        if (!candidateByEmail[email]) candidateByEmail[email] = [];
        candidateByEmail[email].push(r);
      }
    });

    // Cross-reference each HubSpot lead
    const matched = [];
    const unmatched = [];

    hubspotContacts.forEach(contact => {
      const email = (contact.email || '').toLowerCase().trim();
      if (!email) return;

      const candidates = candidateByEmail[email];
      if (candidates && candidates.length > 0) {
        const candidate = candidates[0]; // Take first match
        const hasApplied = !!candidate['Applied'];
        const hasEnrolled = !!candidate['Enrolled'];
        const hasHired = !!candidate['Hired'];
        const hasDropped = !!candidate['Dropped Date'];

        matched.push({
          ...contact,
          candidate,
          hasApplied,
          hasEnrolled,
          hasHired,
          hasDropped,
          stage: hasHired ? 'Hired' : hasEnrolled ? 'Enrolled' : hasApplied ? 'Applied' : 'In Pipeline',
        });
      } else {
        unmatched.push(contact);
      }
    });

    // Stage counts
    const applied = matched.filter(m => m.hasApplied);
    const enrolled = matched.filter(m => m.hasEnrolled);
    const hired = matched.filter(m => m.hasHired);
    const dropped = matched.filter(m => m.hasDropped);

    // Conversion rates
    const formToApplyRate = hubspotContacts.length > 0 ? (applied.length / hubspotContacts.length * 100) : 0;
    const applyToEnrollRate = applied.length > 0 ? (enrolled.length / applied.length * 100) : 0;
    const enrollToHiredRate = enrolled.length > 0 ? (hired.length / enrolled.length * 100) : 0;
    const formToEnrollRate = hubspotContacts.length > 0 ? (enrolled.length / hubspotContacts.length * 100) : 0;

    // Time analysis — how long from form submission to application
    const timeToApply = [];
    matched.filter(m => m.hasApplied && m.createDate).forEach(m => {
      const formDate = new Date(m.createDate);
      const applyDate = new Date(m.candidate['Applied']);
      if (!isNaN(formDate) && !isNaN(applyDate)) {
        const days = Math.round((applyDate - formDate) / (1000 * 60 * 60 * 24));
        if (days >= 0 && days < 365) timeToApply.push(days);
      }
    });
    const medianTimeToApply = timeToApply.length > 0
      ? timeToApply.sort((a, b) => a - b)[Math.floor(timeToApply.length / 2)]
      : null;

    // Monthly trend — form submissions by month
    const monthlyForm = {};
    const monthlyApplied = {};
    hubspotContacts.forEach(c => {
      if (!c.createDate) return;
      const d = new Date(c.createDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyForm[key] = (monthlyForm[key] || 0) + 1;
    });
    matched.filter(m => m.hasApplied).forEach(m => {
      if (!m.candidate['Applied']) return;
      const d = new Date(m.candidate['Applied']);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyApplied[key] = (monthlyApplied[key] || 0) + 1;
    });

    const allMonths = [...new Set([...Object.keys(monthlyForm), ...Object.keys(monthlyApplied)])].sort();
    const last12 = allMonths.slice(-12);
    const monthlyTrend = last12.map(month => ({
      month,
      label: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      formSubmissions: monthlyForm[month] || 0,
      applications: monthlyApplied[month] || 0,
    }));

    // State breakdown
    const stateBreakdown = {};
    hubspotContacts.forEach(c => {
      const state = (c.state || 'Unknown').trim();
      if (!stateBreakdown[state]) stateBreakdown[state] = { form: 0, applied: 0, enrolled: 0 };
      stateBreakdown[state].form++;
    });
    matched.filter(m => m.hasApplied).forEach(m => {
      const state = (m.candidate['Certification state'] || m.state || 'Unknown').trim();
      if (!stateBreakdown[state]) stateBreakdown[state] = { form: 0, applied: 0, enrolled: 0 };
      stateBreakdown[state].applied++;
    });
    matched.filter(m => m.hasEnrolled).forEach(m => {
      const state = (m.candidate['Certification state'] || m.state || 'Unknown').trim();
      if (stateBreakdown[state]) stateBreakdown[state].enrolled++;
    });

    const topStates = Object.entries(stateBreakdown)
      .filter(([s]) => s !== 'Unknown' && s !== '')
      .sort((a, b) => b[1].form - a[1].form)
      .slice(0, 10);

    return {
      totalForm: hubspotContacts.length,
      matched,
      unmatched,
      applied,
      enrolled,
      hired,
      dropped,
      formToApplyRate,
      applyToEnrollRate,
      enrollToHiredRate,
      formToEnrollRate,
      medianTimeToApply,
      monthlyTrend,
      topStates,
      timeToApply,
    };
  }, [hubspotContacts, candidateData]);

  if (loading) {
    return (
      <div className="hf-loading">
        <RefreshCw size={24} className="hf-spinner" />
        <span>Loading HubSpot data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hf-map-card" style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ color: '#EF4444', marginBottom: '1rem', fontSize: '1.1rem' }}>Failed to load HubSpot data</div>
        <div style={{ color: 'rgba(199, 210, 254, 0.6)', marginBottom: '1rem' }}>{error}</div>
        <button onClick={fetchHubspot} className="hf-zoom-out-btn" style={{ position: 'relative' }}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="hf-map-card" style={{ textAlign: 'center', padding: '2rem', color: 'rgba(199, 210, 254, 0.5)' }}>
        Waiting for data...
      </div>
    );
  }

  const maxMonthly = Math.max(...analysis.monthlyTrend.map(m => Math.max(m.formSubmissions, m.applications)), 1);

  return (
    <div className="hf-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Marketing Metrics <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'rgba(199, 210, 254, 0.5)' }}>(For Presentation Only)</span></h2>
          <p style={{ color: 'rgba(199, 210, 254, 0.7)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>Facebook In App Lead Form → Application → Enrollment → Hired</p>
          {lastFetched && (
            <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.35)' }}>
              Last synced: {new Date(lastFetched).toLocaleString()}
            </span>
          )}
        </div>
        <button onClick={fetchHubspot} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          background: 'rgba(0, 190, 168, 0.15)', border: '1px solid rgba(0, 190, 168, 0.3)',
          color: '#4ADE80', borderRadius: '6px', padding: '0.4rem 0.75rem',
          fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Funnel KPIs */}
      <div className="hf-kpi-row" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #818CF8' }}>
          <div className="hf-kpi-value" style={{ color: '#818CF8' }}>{analysis.totalForm.toLocaleString()}</div>
          <div className="hf-kpi-label">Form Submissions</div>
          <div className="hf-kpi-sub">Facebook In App Lead Form</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #60A5FA' }}>
          <div className="hf-kpi-value" style={{ color: '#60A5FA' }}>{analysis.matched.length.toLocaleString()}</div>
          <div className="hf-kpi-label">Found in Pipeline</div>
          <div className="hf-kpi-sub">{(analysis.matched.length / analysis.totalForm * 100).toFixed(1)}% match rate</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #00BEA8' }}>
          <div className="hf-kpi-value" style={{ color: '#00BEA8' }}>{analysis.applied.length.toLocaleString()}</div>
          <div className="hf-kpi-label">Applied</div>
          <div className="hf-kpi-sub">{analysis.formToApplyRate.toFixed(1)}% of form fills</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #4ADE80' }}>
          <div className="hf-kpi-value" style={{ color: '#4ADE80' }}>{analysis.enrolled.length.toLocaleString()}</div>
          <div className="hf-kpi-label">Enrolled</div>
          <div className="hf-kpi-sub">{analysis.applyToEnrollRate.toFixed(1)}% of applied</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #FBBF24' }}>
          <div className="hf-kpi-value" style={{ color: '#FBBF24' }}>{analysis.hired.length.toLocaleString()}</div>
          <div className="hf-kpi-label">Hired</div>
          <div className="hf-kpi-sub">{analysis.enrollToHiredRate.toFixed(1)}% of enrolled</div>
        </div>
        <div className="hf-kpi" style={{ borderLeft: '3px solid #EF4444' }}>
          <div className="hf-kpi-value" style={{ color: '#EF4444' }}>{analysis.unmatched.length.toLocaleString()}</div>
          <div className="hf-kpi-label">Not Yet Applied</div>
          <div className="hf-kpi-sub">{(analysis.unmatched.length / analysis.totalForm * 100).toFixed(1)}% of form fills</div>
        </div>
      </div>

      {/* Conversion Funnel Visual */}
      <div className="hf-map-card">
        <h3 className="hf-section-title">
          <TrendingUp size={18} />
          Conversion Funnel — Facebook Lead Form to Hired
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1.5rem 0' }}>
          {[
            { label: 'Form Fills', count: analysis.totalForm, color: '#818CF8', pct: '100%' },
            { label: 'In Pipeline', count: analysis.matched.length, color: '#60A5FA', pct: `${(analysis.matched.length / analysis.totalForm * 100).toFixed(1)}%` },
            { label: 'Applied', count: analysis.applied.length, color: '#00BEA8', pct: `${analysis.formToApplyRate.toFixed(1)}%` },
            { label: 'Enrolled', count: analysis.enrolled.length, color: '#4ADE80', pct: `${analysis.formToEnrollRate.toFixed(1)}%` },
            { label: 'Hired', count: analysis.hired.length, color: '#FBBF24', pct: `${(analysis.hired.length / analysis.totalForm * 100).toFixed(1)}%` },
          ].map((stage, i, arr) => {
            const widthPct = Math.max(15, (stage.count / analysis.totalForm) * 100);
            return (
              <React.Fragment key={stage.label}>
                {i > 0 && (
                  <div style={{ color: 'rgba(199, 210, 254, 0.3)', fontSize: '1.2rem', flexShrink: 0 }}>→</div>
                )}
                <div style={{
                  textAlign: 'center',
                  flex: `0 0 ${widthPct}%`,
                  maxWidth: '18%',
                }}>
                  <div style={{
                    background: `${stage.color}22`,
                    border: `1px solid ${stage.color}44`,
                    borderRadius: '8px',
                    padding: '0.75rem 0.5rem',
                    marginBottom: '0.35rem',
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: stage.color }}>{stage.count.toLocaleString()}</div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.7)', marginTop: '0.15rem' }}>{stage.pct}</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.7)', fontWeight: 600 }}>{stage.label}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {analysis.medianTimeToApply !== null && (
          <div style={{ textAlign: 'center', fontSize: '0.8rem', color: 'rgba(199, 210, 254, 0.5)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.75rem' }}>
            <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
            Median time from form fill to application: <strong style={{ color: '#00BEA8' }}>{analysis.medianTimeToApply} days</strong>
          </div>
        )}
      </div>

      {/* Monthly Trend — collapsible */}
      {analysis.monthlyTrend.length > 0 && (
        <CollapsibleSection title="Monthly Trend — Form Submissions vs Applications" icon={<Calendar size={18} />}>
          <div style={{ padding: '0.5rem 0' }}>
            {analysis.monthlyTrend.map((month) => (
              <div key={month.month} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr 60px', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                <span style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.6)' }}>{month.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    height: '16px',
                    width: `${(month.formSubmissions / maxMonthly * 100)}%`,
                    background: 'linear-gradient(90deg, rgba(129, 140, 248, 0.4), rgba(129, 140, 248, 0.8))',
                    borderRadius: '3px',
                    minWidth: month.formSubmissions > 0 ? '4px' : '0',
                  }}></div>
                  <span style={{ fontSize: '0.7rem', color: '#818CF8', whiteSpace: 'nowrap' }}>{month.formSubmissions}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    height: '16px',
                    width: `${(month.applications / maxMonthly * 100)}%`,
                    background: 'linear-gradient(90deg, rgba(0, 190, 168, 0.4), rgba(0, 190, 168, 0.8))',
                    borderRadius: '3px',
                    minWidth: month.applications > 0 ? '4px' : '0',
                  }}></div>
                  <span style={{ fontSize: '0.7rem', color: '#00BEA8', whiteSpace: 'nowrap' }}>{month.applications}</span>
                </div>
                <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.4)', textAlign: 'right' }}>
                  {month.formSubmissions > 0 ? `${(month.applications / month.formSubmissions * 100).toFixed(0)}%` : '—'}
                </span>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr 60px', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.65rem', color: 'rgba(199, 210, 254, 0.4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <span></span>
              <span><span style={{ color: '#818CF8' }}>●</span> Form Fills</span>
              <span><span style={{ color: '#00BEA8' }}>●</span> Applications</span>
              <span style={{ textAlign: 'right' }}>Conv%</span>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* State Breakdown — collapsible */}
      {analysis.topStates.length > 0 && (
        <CollapsibleSection title="Top States — Facebook Lead Form Performance" icon={<MapPin size={18} />} count={analysis.topStates.length}>
          <div style={{ fontSize: '0.8rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px 80px', padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <span>State</span>
              <span style={{ textAlign: 'center' }}>Form Fills</span>
              <span style={{ textAlign: 'center' }}>Applied</span>
              <span style={{ textAlign: 'center' }}>Enrolled</span>
              <span style={{ textAlign: 'center' }}>Conv Rate</span>
            </div>
            {analysis.topStates.map(([state, data]) => (
              <div key={state} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px 80px', padding: '0.4rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)', alignItems: 'center' }}>
                <span style={{ color: 'rgba(199, 210, 254, 0.9)' }}>{state}</span>
                <span style={{ textAlign: 'center', color: '#818CF8' }}>{data.form}</span>
                <span style={{ textAlign: 'center', color: '#00BEA8' }}>{data.applied}</span>
                <span style={{ textAlign: 'center', color: '#4ADE80' }}>{data.enrolled}</span>
                <span style={{ textAlign: 'center', fontWeight: 700, color: data.form > 0 ? (data.applied / data.form > 0.3 ? '#4ADE80' : data.applied / data.form > 0.15 ? '#FBBF24' : '#EF4444') : 'rgba(199,210,254,0.4)' }}>
                  {data.form > 0 ? `${(data.applied / data.form * 100).toFixed(1)}%` : '—'}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Not Yet Applied — collapsible */}
      <CollapsibleSection title="Not Yet Applied" icon={<Users size={18} />} count={analysis.unmatched.length}>
        <div style={{ fontSize: '0.8rem', maxHeight: '400px', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 100px', padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'rgba(15, 23, 42, 0.95)' }}>
            <span>Name</span>
            <span>Email</span>
            <span>Form Date</span>
            <span>State</span>
          </div>
          {analysis.unmatched
            .sort((a, b) => new Date(b.createDate) - new Date(a.createDate))
            .slice(0, 100)
            .map((lead, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 100px', padding: '0.35rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)', alignItems: 'center' }}>
              <span style={{ color: 'rgba(199, 210, 254, 0.9)', fontSize: '0.78rem' }}>
                {lead.firstName} {lead.lastName}
              </span>
              <span style={{ color: 'rgba(199, 210, 254, 0.6)', fontSize: '0.75rem' }}>{lead.email}</span>
              <span style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.75rem' }}>
                {lead.createDate ? new Date(lead.createDate).toLocaleDateString() : '—'}
              </span>
              <span style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.75rem' }}>{lead.state || '—'}</span>
            </div>
          ))}
          {analysis.unmatched.length > 100 && (
            <div style={{ padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.4)', fontSize: '0.75rem', textAlign: 'center' }}>
              Showing 100 of {analysis.unmatched.length} — sorted by most recent
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Applied but Not Enrolled — collapsible */}
      {analysis.applied.filter(m => !m.hasEnrolled).length > 0 && (
        <CollapsibleSection title="Applied but Not Yet Enrolled" icon={<Filter size={18} />} count={analysis.applied.filter(m => !m.hasEnrolled).length}>
          <div style={{ fontSize: '0.8rem', maxHeight: '300px', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 120px', padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'rgba(15, 23, 42, 0.95)' }}>
              <span>Name</span>
              <span>Email</span>
              <span>Form Date</span>
              <span>Applied Date</span>
            </div>
            {analysis.applied
              .filter(m => !m.hasEnrolled)
              .sort((a, b) => new Date(b.candidate['Applied']) - new Date(a.candidate['Applied']))
              .slice(0, 50)
              .map((lead, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 120px', padding: '0.35rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)', alignItems: 'center' }}>
                <span style={{ color: 'rgba(199, 210, 254, 0.9)', fontSize: '0.78rem' }}>
                  {lead.firstName} {lead.lastName}
                </span>
                <span style={{ color: 'rgba(199, 210, 254, 0.6)', fontSize: '0.75rem' }}>{lead.email}</span>
                <span style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.75rem' }}>
                  {lead.createDate ? new Date(lead.createDate).toLocaleDateString() : '—'}
                </span>
                <span style={{ color: '#00BEA8', fontSize: '0.75rem' }}>
                  {lead.candidate['Applied'] ? new Date(lead.candidate['Applied']).toLocaleDateString() : '—'}
                </span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default MarketingMetrics;
